import { useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { SignalPost, SignalPostRow } from "../types/signal";
import { toSignalPost } from "../types/signal";

interface UseSignalPostsReturn {
  posts: SignalPost[];
  loading: boolean;
  error: string | null;
  fetchPosts: (filters?: SignalPostFilters) => Promise<SignalPost[]>;
  getPostBySlug: (slug: string) => Promise<SignalPost | null>;
  updatePostVoteScore: (postId: string, delta: number, upDelta: number, downDelta: number) => void;
}

interface SignalPostFilters {
  kind?: string;
  search?: string;
  tag?: string;
  limit?: number;
}

export const useSignalPosts = (): UseSignalPostsReturn => {
  const [posts, setPosts] = useState<SignalPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async (filters?: SignalPostFilters): Promise<SignalPost[]> => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = filters?.limit ?? 50;
      let query = supabase
        .from("signal_posts")
        .select("*")
        .eq("is_published", true)
        .order("date", { ascending: false })
        .limit(pageSize);

      if (filters?.kind) {
        query = query.eq("kind", filters.kind);
      }
      if (filters?.tag) {
        query = query.contains("tags", [filters.tag]);
      }
      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%`);
      }

      const { data, error: supabaseError } = await query;
      if (supabaseError) throw supabaseError;

      const mapped = (data as SignalPostRow[]).map(toSignalPost);
      setPosts(mapped);
      return mapped;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch signal posts";
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getPostBySlug = useCallback(async (slug: string): Promise<SignalPost | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supabaseError } = await supabase
        .from("signal_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (supabaseError) {
        // PostgREST returns code "PGRST116" when `.single()` matches zero
        // rows. That's a legitimate not-found, not a query failure - keep
        // `error` null so the page can render a 404 state instead of a
        // generic "something went wrong".
        const code = (supabaseError as { code?: string }).code;
        if (code === "PGRST116") return null;
        throw supabaseError;
      }
      return toSignalPost(data as SignalPostRow);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch signal post";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePostVoteScore = useCallback(
    (postId: string, delta: number, upDelta: number, downDelta: number) => {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                voteScore: p.voteScore + delta,
                upvoteCount: p.upvoteCount + upDelta,
                downvoteCount: p.downvoteCount + downDelta,
              }
            : p
        )
      );
    },
    []
  );

  return { posts, loading, error, fetchPosts, getPostBySlug, updatePostVoteScore };
};
